import type React from 'react';

import type { CardId, SampleId } from '@vlabs/api/artemy/model';
import { getArtemySampleUrl } from '@vlabs/api/axios/point2-client';
import { DetectionIcon } from '@vlabs/icons';
import { calcSimilarityPercent } from '@vlabs/shared/utils';
import { Control, FaceSample, Tag } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import { GoToCardCircleAnimatedButton } from '@vlabs/monokit/features/go-to-card';
import { CardSamplePoint3 } from '@vlabs/monokit/shared/ui/card-sample';

import st from './AttachSimilarFaceToCard.module.sass';

interface AttachSimilarFaceToCardPoint3Props {
  cardId: CardId;
  isPending?: boolean;
  onSubmit: () => void;
  sampleId: SampleId;
  similarity?: number;
}

function AttachSimilarFaceToCardPoint3({
  cardId,
  isPending = false,
  onSubmit,
  sampleId,
  similarity = undefined,
}: AttachSimilarFaceToCardPoint3Props): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={st.Root}>
      <div className={st.Header}>
        <span className={st.Title}>
          {t('features:attach_similar_face_to_card.title')}
        </span>

        <span className={st.Subtitle}>
          {t('features:attach_similar_face_to_card.subtitle')}
        </span>
      </div>

      <div className={st.Body}>
        <div className={st.SamplesWrapper}>
          {similarity !== undefined && (
            <Tag
              className={st.SimilarityTag}
              icon={<DetectionIcon />}
              kind="positive"
              size="large"
              value={String(calcSimilarityPercent(similarity))}
            />
          )}

          <div className={st.Sample}>
            <FaceSample
              className={st.FaceSample}
              size="large"
              src={getArtemySampleUrl('faces', sampleId)}
            />
            <span className={st.Description}>
              {t('features:attach_similar_face_to_card.face')}
            </span>
          </div>

          <div className={st.Sample}>
            <CardSamplePoint3
              buttonOnBottom={(
                <GoToCardCircleAnimatedButton
                  cardId={cardId}
                  size="small"
                />
              )}
              cardId={cardId}
              className={st.FaceSample}
              size="large"
            />
            <span className={st.Description}>
              {t('features:attach_similar_face_to_card.card')}
            </span>
          </div>
        </div>
      </div>

      <Control.Button
        className={st.ButtonWrapper}
        disabled={isPending}
        onClick={onSubmit}
      >
        {t('features:attach_similar_face_to_card.buttons.add')}
      </Control.Button>
    </div>
  );
}

export { AttachSimilarFaceToCardPoint3 };
